import { KoobButton } from '@koob/ui';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { put } from '../../../api/node';
import { useApp, useSnack } from '../../../hooks';
import CompatibilityRecapModal from './CompatibilityRecapModal';
import CompatibilitySearchPanel from './CompatibilitySearchPanel';
import { ExperienceCompatibilityTypeEnum, RoleEnum } from '@koob/enums';

const AssignmentColumn = ({
  targetKey,
  isActive,
  isBest,
  readonlyList,
  values,
  setFieldValue,
  isEditable,
  t,
}) => {
  const displayList = readonlyList || values[targetKey] || [];
  const borderColor = isBest ? 'border-emerald-400' : 'border-red-400';
  const headerColor = isBest ? 'text-emerald-600' : 'text-red-600';
  const iconClass = isBest ? 'fa-circle-check' : 'fa-circle-xmark';

  const title = isBest
    ? targetKey === 'step1_best'
      ? t('compatibility.columns.bestFor.title')
      : t('compatibility.columns.bestTo.title')
    : targetKey === 'step2_black'
      ? t('compatibility.columns.blackFor.title')
      : t('compatibility.columns.blackTo.title');

  const subtitle = isBest
    ? targetKey === 'step1_best'
      ? t('compatibility.columns.bestFor.subtitle')
      : t('compatibility.columns.bestTo.subtitle')
    : targetKey === 'step2_black'
      ? t('compatibility.columns.blackFor.subtitle')
      : t('compatibility.columns.blackTo.subtitle');

  const emptyLabel = isBest
    ? t('compatibility.columns.emptyBest')
    : t('compatibility.columns.emptyBlack');

  return (
    <div
      className={`border-2 rounded-xl p-4 shadow-sm flex flex-col h-[650px] bg-white transition-opacity ${
        isActive
          ? borderColor
          : 'border-slate-200 opacity-50 pointer-events-none'
      }`}
    >
      <div className="flex justify-between items-start mb-1">
        <h3
          className={`font-bold text-xs flex items-center gap-1.5 ${
            isActive ? headerColor : 'text-slate-400'
          }`}
        >
          <i className={`fa-light ${iconClass}`}></i>
          {title}
        </h3>
        {isActive && isEditable && !readonlyList && (
          <KoobButton size="sm" onClick={() => setFieldValue(targetKey, [])}>
            {t('compatibility.columns.removeAll')}
          </KoobButton>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mb-1">{subtitle}</p>
      <p className="text-[11px] text-slate-500 font-medium mb-3">
        {t('compatibility.columns.programCount', {
          count: displayList.length,
        })}
      </p>

      <div className="flex-1 overflow-y-auto space-y-2">
        {displayList.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-slate-300 italic">{emptyLabel}</p>
          </div>
        ) : (
          displayList.map((exp) => (
            <div
              key={exp.id}
              className={`border rounded-xl p-3 flex justify-between items-start gap-2 ${
                isActive
                  ? isBest
                    ? 'border-emerald-100 bg-emerald-50/20'
                    : 'border-red-100 bg-red-50/20'
                  : 'border-slate-100 bg-slate-50/40'
              }`}
            >
              <div className="min-w-0">
                <span
                  className={`text-xs font-semibold truncate block ${
                    isActive ? 'text-slate-700' : 'text-slate-400'
                  }`}
                >
                  {exp.name}
                </span>
                <span className="text-[11px] text-slate-400 mt-0.5 block">
                  {exp.startLocation?.title || exp.inCity?.title} →{' '}
                  {exp.endLocation?.title || exp.inCity?.title}
                </span>
                {exp.supplier?.name && (
                  <span className="text-[10px] text-slate-300 uppercase tracking-wider font-medium">
                    {exp.supplier.name}
                  </span>
                )}
              </div>
              {isActive && isEditable && !readonlyList && (
                <span
                  onClick={() =>
                    setFieldValue(
                      targetKey,
                      (values[targetKey] || []).filter((e) => e.id !== exp.id),
                    )
                  }
                  className="flex-shrink-0 text-gray-300 hover:text-red-400 transition mt-0.5"
                >
                  <i className="fa-light fa-xmark text-xs"></i>
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default function CompatibilityForm({
  experience,
  isSaved,
  setIsSaved,
  currentStep,
  setCurrentStep,
  showRecap,
  setShowRecap,
  reloadExperience,
}) {
  const { values, setFieldValue, setSubmitting, isSubmitting, resetForm } =
    useFormikContext();
  const { notify } = useSnack();
  const { currentUserRole } = useApp();
  const { experienceId } = useParams();
  const { t } = useTranslation('experiences');
  const totalSteps = 4;

  const STEP_LABELS = {
    1: {
      title: t('compatibility.steps.1.title', 'Best match for this experience'),
      subtitle: t('compatibility.steps.1.subtitle'),
      type: 'best-match',
    },
    2: {
      title: t('compatibility.steps.2.title', 'Black list for this experience'),
      subtitle: t('compatibility.steps.2.subtitle'),
      type: 'black-list',
    },
    3: {
      title: t('compatibility.steps.3.title', 'Best match to this experience'),
      subtitle: t('compatibility.steps.3.subtitle'),
      type: 'best-match',
    },
    4: {
      title: t('compatibility.steps.4.title', 'Black list to this experience'),
      subtitle: t('compatibility.steps.4.subtitle'),
      type: 'black-list',
    },
  };

  const stepConfig = STEP_LABELS[currentStep];
  const activeKey =
    currentStep === 1
      ? 'step1_best'
      : currentStep === 2
        ? 'step2_black'
        : currentStep === 3
          ? 'step3_best'
          : 'step4_black';

  const assignedIds = useMemo(() => {
    const keys =
      currentStep <= 2
        ? ['step1_best', 'step2_black']
        : ['step3_best', 'step4_black'];
    return new Set(keys.flatMap((key) => values[key] || []).map((e) => e.id));
  }, [values, currentStep]);

  const handleFinalSave = async () => {
    setSubmitting(true);
    try {
      const bestMatches = [
        ...values.step1_best.map((e) => ({
          id: e.id,
          direction: ExperienceCompatibilityTypeEnum.FOR,
        })),
        ...values.step3_best.map((e) => ({
          id: e.id,
          direction: ExperienceCompatibilityTypeEnum.TO,
        })),
      ];
      const incompatibilities = [
        ...values.step2_black.map((e) => ({
          id: e.id,
          direction: ExperienceCompatibilityTypeEnum.FOR,
        })),
        ...values.step4_black.map((e) => ({
          id: e.id,
          direction: ExperienceCompatibilityTypeEnum.TO,
        })),
      ];

      await put(`/experiences/${experienceId}/compatibilities`, {
        bestMatches,
        incompatibilities,
      });
      setIsSaved(true);
      setShowRecap(false);
      notify(t('compatibility.notifications.success'));
      if (reloadExperience) reloadExperience();
    } catch (err) {
      notify(t('compatibility.notifications.error'), {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isEditable = !isSaved && currentStep >= 1;
  const isForContext = currentStep === 1 || currentStep === 2;
  const bestKey = isForContext ? 'step1_best' : 'step3_best';
  const blackKey = isForContext ? 'step2_black' : 'step4_black';
  const isBestActive = currentStep === 1 || currentStep === 3;
  const isBlackActive = currentStep === 2 || currentStep === 4;

  return (
    <>
      {isSaved ? (
        <div>
          <div className="rounded-xl mb-4 flex items-center justify-between text-xs font-semibold">
            <div className="flex items-center gap-2 text-green-500">
              <i className="fa-light fa-lock"></i>
              {t('compatibility.readonlyBanner')}
            </div>

            <div className="flex items-center gap-2">
              <KoobButton onClick={() => setShowRecap(true)}>
                <i className="fa-light fa-eye"></i>
                {t('compatibility.actions.viewRecap')}
              </KoobButton>

              <KoobButton
                onClick={() => {
                  setIsSaved(false);
                  setCurrentStep(1);
                }}
              >
                <i className="fa-light fa-pen-to-square"></i>
                {t('compatibility.actions.edit')}
              </KoobButton>
            </div>
          </div>
        </div>
      ) : currentStep === 0 ? (
        <div className="flex justify-between items-center mb-4 mt-2">
          <span className="text-xs font-semibold text-slate-700">
            {t('compatibility.startMessage')}
          </span>

          <div className="flex items-center space-x-2">
            <KoobButton
              variant="simple"
              size="sm"
              type="button"
              disabled
              onClick={() => resetForm()}
            >
              {t('compatibility.actions.cancel')}
            </KoobButton>

            <KoobButton
              variant="primary"
              size="sm"
              type="button"
              disabled={currentUserRole === RoleEnum.KOOB_ADMIN}
              onClick={() => setCurrentStep(1)}
            >
              <span className="flex items-center gap-2">
                <i className="fa-light fa-play text-[10px]"></i>
                {t('compatibility.actions.start', 'Start')}
              </span>
            </KoobButton>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center mb-4 mt-2">
          <span className="text-xs font-semibold text-slate-700">
            {t('compatibility.stepIndicator', 'Step {{current}}/{{total}} —', {
              current: currentStep,
              total: totalSteps,
            })}{' '}
            <span className="text-orange-500 font-bold">
              {stepConfig.title}
            </span>
          </span>

          <div className="flex items-center space-x-2">
            <KoobButton
              size="sm"
              onClick={() => {
                setShowRecap(false);
                resetForm();
                setCurrentStep(0);
              }}
            >
              {t('compatibility.actions.cancel')}
            </KoobButton>

            <KoobButton
              variant="primary"
              size="sm"
              onClick={() => {
                if (currentStep < totalSteps) {
                  setCurrentStep((prev) => prev + 1);
                } else {
                  setShowRecap(true);
                }
              }}
            >
              {currentStep === totalSteps ? (
                <>
                  <i className="fa-light fa-floppy-disk text-[10px]"></i>
                  {t('compatibility.actions.save')}
                </>
              ) : (
                <>
                  {t('compatibility.actions.saveAndNext')}
                  <i className="fa-light fa-arrow-right text-[10px]"></i>
                </>
              )}
            </KoobButton>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {[1, 2, 3, 4].map((step) => {
          const isActive = currentStep === step;
          const isDone = isSaved || step < currentStep;
          const cfg = STEP_LABELS[step];
          return (
            <button
              key={step}
              type="button"
              disabled={isSaved}
              className={`text-left p-3 rounded-xl flex items-start gap-2.5 transition border-none ${
                isActive
                  ? 'bg-orange-50/40 border-orange-400 shadow-sm'
                  : isDone
                    ? 'bg-emerald-50/20 border-emerald-200'
                    : 'bg-white border-slate-200'
              }`}
            >
              <span
                className={`flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                  isDone
                    ? 'bg-emerald-500 text-white'
                    : isActive
                      ? 'bg-orange-500 text-k-h'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isDone ? (
                  <i className="fa-light fa-check text-[9px]"></i>
                ) : (
                  step
                )}
              </span>
              <div className="min-w-0">
                <div
                  className={`text-xs font-bold truncate ${
                    isDone
                      ? 'text-emerald-600'
                      : isActive
                        ? 'text-orange-500'
                        : 'text-slate-700'
                  }`}
                >
                  {cfg.title}
                </div>
                <div className="text-[10px] text-slate-600 truncate mt-0.5 leading-snug">
                  {cfg.subtitle}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <CompatibilitySearchPanel
          experienceId={experienceId}
          experience={experience}
          currentStep={currentStep}
          activeKind={stepConfig?.type}
          assignedIds={assignedIds}
          onAssign={(items) =>
            setFieldValue(activeKey, [...(values[activeKey] || []), ...items])
          }
          isEditable={isEditable}
        />

        <AssignmentColumn
          targetKey={bestKey}
          isActive={isBestActive}
          isBest={true}
          readonlyList={!isBestActive ? values[bestKey] || [] : null}
          values={values}
          setFieldValue={setFieldValue}
          isEditable={isEditable}
          t={t}
        />

        <AssignmentColumn
          targetKey={blackKey}
          isActive={isBlackActive}
          isBest={false}
          readonlyList={null}
          values={values}
          setFieldValue={setFieldValue}
          isEditable={isEditable}
          t={t}
        />
      </div>

      <CompatibilityRecapModal
        isOpen={showRecap}
        onClose={() => setShowRecap(false)}
        values={values}
        onConfirmSaved={handleFinalSave}
        isSaved={isSaved}
        isSaving={isSubmitting}
        experienceName={experience?.name}
      />
    </>
  );
}
